import "./MobileNavigationMenu.scss";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FocusTrap } from "../../../common/components/FocusTrap/FocusTrap";
import { IconClose } from "../../../common/icons/IconClose/IconClose";
import { IconPerfIncome } from "../../../common/icons/IconPerfIncome/IconPerfIncome";
import { IconTwoUsersNav } from "../../../common/icons/IconTwoUsersNav/IconTwoUsersNav";
import { IconAcademy } from "../../../common/icons/IconAcademy/IconAcademy";
import { IconCarrot } from "../../../common/icons/IconCarrot/IconCarrot";
import { NavigationSubMenu } from "../NavigationSubMenu/NavigationSubMenu";
import { perfIncomeItems, groupMgmtNavItems, sectionRoutePaths } from "../navigationData";
import { createNavigationConfig } from "../../../common/util/Navigation/createNavigationConfig";
import type { NavigationApiResponse } from "../../../services/MyBizShell/mybizShellAPI.types";
import defaultContent from "../../../content/navigation";
import { useAppSelector, useAppDispatch } from "../../../store";
import { setActiveSection, setExpandedSection } from "../../../reducers/navigationSelection";
import { shouldDisplayAcademyLink, useAcademyLink } from "../../Academy/useAcademyLink";
import { NewNavSmartNextSteps } from "../../NewNavSmartNextSteps/NewNavSmartNextSteps";
import { MobileNavigationMenuSkeleton } from "./MobileNavigationMenuSkeleton";

interface MobileNavigationMenuProps {
  onClose: () => void;
  navigationData?: NavigationApiResponse;
  isLoading?: boolean;
}

export const MobileNavigationMenu = ({ onClose, navigationData, isLoading }: MobileNavigationMenuProps) => {
  const { t } = useTranslation(["navigation"]);
  const labelPerfIncome = `${t("performanceAndIncome", defaultContent.performanceAndIncome)}`;
  const labelGroupMgmt = `${t("groupManagement", defaultContent.groupManagement)}`;
  const labelAcademy = `${t("academy", defaultContent.academy)}`;
  const labelFreqUsed = `${t("frequentlyUsed", defaultContent.frequentlyUsed)}`;
  const dispatch = useAppDispatch();
  const { shellConfig } = useAppSelector((state) => state.boot);
  const { activeSection, expandedSection } = useAppSelector((state) => state.navigationSelection);
  const { menuConfig = [] } = shellConfig;
  const [isClosing, setIsClosing] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { configuration, user } = useAppSelector((state) => state.boot);
  const mobileNavConfig = navigationData ? createNavigationConfig(navigationData, undefined, user.aff) : null;

  const activePerformanceIncome = mobileNavConfig?.performanceIncome ?? perfIncomeItems;
  const activeGroupMgmtNavItems =
    mobileNavConfig?.groupMgmtNavItems ??
    (shellConfig.hasAchievedEmeraldOrHigher
      ? groupMgmtNavItems
      : groupMgmtNavItems.filter((item) => item.id !== "edlos"));
  const showAcademy = navigationData ? navigationData.Academy?.Id === "academy" : shouldDisplayAcademyLink(menuConfig);
  const academyBaseLink = configuration?.externalIntegration?.academy?.navigationlink ?? "";
  const academyLink = user?.cognitoToken ? `${academyBaseLink}?q=${user.cognitoToken}` : academyBaseLink;
  const { handleAcademyClick } = useAcademyLink();

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  useEffect(() => {
    const hash = location.hash.replace("#", "");
    if (hash && hash in sectionRoutePaths) {
      dispatch(setActiveSection(hash));
      return;
    }
    const currentPath = location.pathname;
    for (const [section, paths] of Object.entries(sectionRoutePaths)) {
      if (paths.some((path) => currentPath.startsWith(path))) {
        dispatch(setActiveSection(section));
        return;
      }
    }
  }, [location.pathname, location.hash, dispatch]);

  const handleClose = useCallback(() => {
    dispatch(setExpandedSection(null));
    setIsClosing(true);
  }, [dispatch]);

  const handleAnimationEnd = useCallback(
    (event: React.AnimationEvent<HTMLElement>) => {
      if (isClosing && event.animationName === "slideUp" && event.target === event.currentTarget) {
        onClose();
      }
    },
    [isClosing, onClose],
  );

  const handleNavItemClick = useCallback(
    (submenu: string | null, directPath?: string) => {
      if (directPath) {
        navigate({ pathname: directPath });
        handleClose();
        return;
      }
      if (submenu) {
        dispatch(setExpandedSection(submenu));
      }
    },
    [navigate, handleClose, dispatch],
  );

  const handleSubmenuBack = useCallback(() => {
    dispatch(setExpandedSection(null));
  }, [dispatch]);

  const handleSectionActivated = useCallback(
    (sectionId: string) => {
      dispatch(setActiveSection(sectionId));
    },
    [dispatch],
  );

  const menuClass = `MobileNavigationMenu${isClosing ? " MobileNavigationMenu--closing" : ""}`;

  return (
    <>
      <FocusTrap
        handleOnClose={handleClose}
        className={menuClass}
        role="dialog"
        aria-modal={true}
        aria-label="Navigation menu"
        aria-hidden={expandedSection !== null}
        onAnimationEnd={handleAnimationEnd}
        data-testid="mobile-navigation-menu"
      >
        <div className="MobileNavigationMenu__header">
          <button
            type="button"
            className="MobileNavigationMenu__close-button"
            onClick={handleClose}
            aria-label="Close navigation menu"
            data-testid="mobile-menu-close"
          >
            <IconClose width="1.5rem" height="1.5rem" color="white" />
          </button>
        </div>

        <div className="MobileNavigationMenu__content">
          <nav className="MobileNavigationMenu__nav-list" aria-label="Main navigation">
            {isLoading ? (
              <MobileNavigationMenuSkeleton />
            ) : (
              <>
                <button
                  type="button"
                  className={`MobileNavigationMenu__nav-item${activeSection === "performance" ? " MobileNavigationMenu__nav-item--active" : ""}`}
                  onClick={() => handleNavItemClick("performance")}
                  data-testid="mobile-nav-performance"
                >
                  <span className="MobileNavigationMenu__nav-item-left">
                    <span className="MobileNavigationMenu__nav-icon">
                      <IconPerfIncome />
                    </span>
                    <span className="MobileNavigationMenu__nav-label">{labelPerfIncome}</span>
                  </span>
                  <span className="MobileNavigationMenu__nav-chevron">
                    <IconCarrot orientation="right" color="white" />
                  </span>
                </button>

                <button
                  type="button"
                  className={`MobileNavigationMenu__nav-item${activeSection === "group-management" ? " MobileNavigationMenu__nav-item--active" : ""}`}
                  onClick={() => handleNavItemClick("group-management")}
                  data-testid="mobile-nav-group-management"
                >
                  <span className="MobileNavigationMenu__nav-item-left">
                    <span className="MobileNavigationMenu__nav-icon">
                      <IconTwoUsersNav color="white" />
                    </span>
                    <span className="MobileNavigationMenu__nav-label">{labelGroupMgmt}</span>
                  </span>
                  <span className="MobileNavigationMenu__nav-chevron">
                    <IconCarrot orientation="right" color="white" />
                  </span>
                </button>

                {showAcademy && (
                  <a
                    href={academyLink}
                    rel="noopener noreferrer"
                    aria-label={labelAcademy}
                    className={`MobileNavigationMenu__nav-item${activeSection === "academy" ? " MobileNavigationMenu__nav-item--active" : ""}`}
                    onClick={(e) => {
                      handleAcademyClick(academyLink, e);
                      handleClose();
                    }}
                  >
                    <span className="MobileNavigationMenu__nav-item-left">
                      <span className="MobileNavigationMenu__nav-icon">
                        <IconAcademy color="white" />
                      </span>
                      <span className="MobileNavigationMenu__nav-label">{labelAcademy}</span>
                    </span>
                  </a>
                )}
              </>
            )}
          </nav>

          <div className="MobileNavigationMenu__divider" />

          <div className="MobileNavigationMenu__frequently-used">
            <span className="MobileNavigationMenu__section-heading">{labelFreqUsed}</span>
            <NewNavSmartNextSteps onClose={handleClose} navigationData={navigationData} />
          </div>
        </div>
      </FocusTrap>

      {expandedSection === "performance" && (
        <NavigationSubMenu
          title={labelPerfIncome}
          sectionId="performance"
          navItems={activePerformanceIncome}
          onBack={handleSubmenuBack}
          onClose={handleClose}
          onActivate={handleSectionActivated}
        />
      )}

      {expandedSection === "group-management" && (
        <NavigationSubMenu
          title={labelGroupMgmt}
          sectionId="group-management"
          navItems={activeGroupMgmtNavItems}
          onBack={handleSubmenuBack}
          onClose={handleClose}
          onActivate={handleSectionActivated}
        />
      )}
    </>
  );
};
